import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useBranch } from "@/context/BranchContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS } from "@/lib/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { PackageX, RefreshCw, Download, PlusCircle, AlertTriangle, Banknote } from "lucide-react";

export default function MissedSales() {
  const { branches, activeBranch } = useBranch();
  const { can } = useAuth();
  const [branchId, setBranchId] = useState(activeBranch !== "all" ? activeBranch : "all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [trend, setTrend] = useState([]);
  const [lostRevenue, setLostRevenue] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [threshold, setThreshold] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const canPromote = can("products.manage");

  const params = useCallback(() => {
    const p = {};
    if (branchId && branchId !== "all") p.branch_id = branchId;
    if (from) p.date_from = from;
    if (to) p.date_to = to;
    return p;
  }, [branchId, from, to]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/missed-sales/report", { params: params() })
      .then((r) => {
        setRows(r.data.rows || []);
        setTrend(r.data.trend || []);
        setLostRevenue(r.data.total_lost_revenue || 0);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
    api.get("/missed-sales/alerts", { params: branchId && branchId !== "all" ? { branch_id: branchId } : {} })
      .then((r) => { setAlerts(r.data.alerts || []); setThreshold(r.data.threshold || 0); })
      .catch(() => {});
  }, [params, branchId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/missed-sales/report/download", { params: params(), responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "mobimed_missed_sales.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel file downloaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const promote = async (name) => {
    setPromoting(name);
    try {
      const r = await api.post("/missed-sales/promote", { product_name: name, cost_price: 0 });
      toast.success(`Added "${name}" to catalog (${r.data.product.sku}). Set its cost price in Catalog.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPromoting(null);
    }
  };

  const totalQty = rows.reduce((s, r) => s + (r.total_quantity || 0), 0);

  return (
    <div className="space-y-6" data-testid="missed-sales-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground flex items-center gap-2"><PackageX className="h-6 w-6 text-rose-500" /> Out-of-stock demand</h1>
          <p className="text-muted-foreground text-sm">Medicines customers asked for but we couldn't supply — ranked missed sales to recover through purchasing.</p>
        </div>
        <Button variant="outline" onClick={download} disabled={downloading || !rows.length} data-testid="missed-download-btn">
          <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Preparing…" : "Export Excel"}
        </Button>
      </div>

      {alerts.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30" data-testid="missed-alerts">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-semibold text-sm mb-2">
            <AlertTriangle className="h-4 w-4" /> {alerts.length} medicine(s) crossed the demand threshold ({threshold}+ units in 30 days) — flag to procurement before auto-POs run
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.map((a, i) => (
              <span key={i} className="text-xs rounded-full px-2.5 py-1 bg-white dark:bg-background border border-amber-200 dark:border-amber-500/30" data-testid={`alert-chip-${i}`}>
                {a.product_name} · <span className="font-semibold">{a.total_quantity}</span> units
                {!a.is_catalog && <span className="text-amber-700 dark:text-amber-400"> (not carried)</span>}
              </span>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Est. lost revenue</span>
            <span className="h-8 w-8 rounded-lg flex items-center justify-center bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300"><Banknote className="h-4 w-4" /></span>
          </div>
          <div className="mt-2 text-2xl font-bold text-foreground" data-testid="missed-lost-revenue">{fmtGHS(lostRevenue)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Out-of-stock items at expected selling price</div>
        </Card>
        <Card className="p-4 md:col-span-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Lost revenue by month</h3>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={48} />
              <Tooltip formatter={(v) => fmtGHS(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="lost_revenue" name="Lost revenue" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          {trend.length === 0 && <div className="text-center text-xs text-muted-foreground -mt-24 mb-20">No data for this filter</div>}
        </Card>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div className="w-56"><Label className="text-xs">Branch</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="missed-filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All my branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="missed-filter-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="missed-filter-to" /></div>
        <Button onClick={load} data-testid="missed-apply"><RefreshCw className="h-4 w-4 mr-1.5" /> Apply</Button>
        <div className="ml-auto text-sm text-muted-foreground">{rows.length} medicine(s) · {totalQty} units requested</div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5">Medicine</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-right px-4 py-2.5">Requests</th>
              <th className="text-right px-4 py-2.5">Qty requested</th>
              <th className="text-right px-4 py-2.5">Est. lost revenue</th>
              <th className="text-left px-4 py-2.5">Branches</th>
              <th className="text-left px-4 py-2.5">Last requested</th>
              {canPromote && <th className="text-right px-4 py-2.5"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={canPromote ? 8 : 7} className="px-4 py-10 text-center text-muted-foreground">{loading ? "Loading…" : "No missed sales recorded for this filter"}</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-muted/30" data-testid={`missed-row-${i}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-foreground">{r.product_name}</div>
                  {r.sku && <div className="text-xs text-muted-foreground font-mono">{r.sku}</div>}
                </td>
                <td className="px-4 py-2.5">
                  {r.is_catalog
                    ? <span className="text-xs rounded-full px-2 py-0.5 bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">Out of stock</span>
                    : <span className="text-xs rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Not carried</span>}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">{r.requests}</td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold">{r.total_quantity}</td>
                <td className="px-4 py-2.5 text-right font-mono">{r.is_catalog ? fmtGHS(r.est_lost_revenue) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{(r.branches || []).join(", ")}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{(r.last_requested || "").slice(0, 10)}</td>
                {canPromote && (
                  <td className="px-4 py-2.5 text-right">
                    {!r.is_catalog && (
                      <Button size="sm" variant="outline" disabled={promoting === r.product_name}
                        onClick={() => promote(r.product_name)} data-testid={`promote-btn-${i}`}>
                        <PlusCircle className="h-3.5 w-3.5 mr-1" /> {promoting === r.product_name ? "Adding…" : "Add to catalog"}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
